import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import SEOHead from '../components/SEOHead';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, documentPageCanvasSx, documentLegalPaperSx, documentEyebrowSx } from '../constants/documentTheme';
import { useTranslation } from 'react-i18next';

const Privacy = () => {
  const { t } = useTranslation();

  return (
    <Box sx={documentPageCanvasSx}>
      <SEOHead
        title="Privacy Policy - Déjà Vu Nomad"
        description="Privacy Policy for Déjà Vu Nomad travel planning platform. Learn how we protect your data."
        keywords="privacy policy, data protection, user privacy, travel planning"
        canonical="https://dejavunomad.com/privacy"
      />
      <DocumentMarketingHero
        eyebrow={t('home.footer.legal')}
        title="Privacy Policy"
        subtitle="How we collect, use, and protect your information on Déjà Vu Nomad."
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper elevation={0} sx={documentLegalPaperSx}>
          <Typography sx={{ ...documentEyebrowSx, mb: 2 }}>
            Your Privacy Matters
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
            At Déjà Vu Nomad, we are committed to protecting your privacy and ensuring the security 
            of your personal information. This Privacy Policy explains how we collect, use, and 
            safeguard your data when you use our travel planning platform.
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Information We Collect
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              <strong>Account Information:</strong> Name, email address, profile information<br/>
              <strong>Travel Data:</strong> Trip preferences, destinations, dates, travel companions<br/>
              <strong>Location Data:</strong> Optional location sharing for real-time coordination<br/>
              <strong>Usage Data:</strong> How you interact with our platform and features<br/>
              <strong>Payment Information:</strong> Processed securely through Stripe (we don't store card details)
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              How We Use Your Information
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              • <strong>Service Delivery:</strong> Provide personalized travel recommendations and trip planning<br/>
              • <strong>Communication:</strong> Send important updates, notifications, and support messages<br/>
              • <strong>Improvement:</strong> Analyze usage patterns to enhance our platform and features<br/>
              • <strong>Security:</strong> Protect against fraud, abuse, and unauthorized access<br/>
              • <strong>Legal Compliance:</strong> Meet regulatory requirements and respond to legal requests
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Data Sharing & Third Parties
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              We do not sell your personal information. We may share data only in these limited circumstances:<br/>
              • <strong>Service Providers:</strong> Trusted partners who help us operate our platform (e.g., Stripe for payments)<br/>
              • <strong>Legal Requirements:</strong> When required by law or to protect our rights<br/>
              • <strong>Consent:</strong> When you explicitly authorize us to share specific information<br/>
              • <strong>Business Transfers:</strong> In the event of a merger or acquisition (with notice)
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Data Security
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              • <strong>Encryption:</strong> All data is encrypted in transit and at rest<br/>
              • <strong>Access Controls:</strong> Strict access controls and authentication measures<br/>
              • <strong>Regular Audits:</strong> Security assessments and vulnerability testing<br/>
              • <strong>Incident Response:</strong> Rapid response to any security incidents<br/>
              • <strong>Staff Training:</strong> Regular privacy and security training for our team
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Your Rights & Choices
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              • <strong>Access:</strong> View and download your personal data<br/>
              • <strong>Correction:</strong> Update or correct inaccurate information<br/>
              • <strong>Deletion:</strong> Request deletion of your account and data<br/>
              • <strong>Portability:</strong> Export your data in a standard format<br/>
              • <strong>Opt-out:</strong> Unsubscribe from marketing communications<br/>
              • <strong>Location Controls:</strong> Disable location sharing at any time
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Cookies & Tracking
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              • <strong>Essential Cookies:</strong> Required for platform functionality<br/>
              • <strong>Analytics:</strong> Help us understand how you use our platform<br/>
              • <strong>Preferences:</strong> Remember your settings and preferences<br/>
              • <strong>Third-party:</strong> Some cookies from trusted partners (with your consent)
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Children's Privacy
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              Our service is not intended for children under 13. We do not knowingly collect 
              personal information from children under 13. If we become aware that we have 
              collected such information, we will take steps to delete it promptly.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              International Data Transfers
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              Your data may be processed in countries other than your own. We ensure 
              appropriate safeguards are in place to protect your information in accordance 
              with applicable data protection laws.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Changes to This Policy
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              We may update this Privacy Policy from time to time. We will notify you of 
              any significant changes via email or through our platform. Your continued 
              use of our service after changes constitutes acceptance of the updated policy.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, mb: 2, color: DOCUMENT.ink }}>
              Contact Us
            </Typography>
            <Typography variant="body2" paragraph sx={{ color: DOCUMENT.ink2, lineHeight: 1.75 }}>
              If you have questions about this Privacy Policy or our data practices, please contact us:<br/>
              • <strong>Email:</strong> info@dejavunomad.com<br/>
              • <strong>Website:</strong> https://dejavunomad.com<br/>
              • <strong>Data Protection Officer:</strong> dpo@dejavunomad.com<br/>
              • <strong>Support:</strong> Available in-app<br/>
              • <strong>Response Time:</strong> Within 24 hours for privacy-related inquiries
            </Typography>
          </Box>

          <Box sx={{ 
            bgcolor: DOCUMENT.canvas, 
            p: 3, 
            border: `1px solid ${DOCUMENT.ruleHard}`,
          }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center', color: DOCUMENT.ink3 }}>
              Last updated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Privacy;
